import { useState, useEffect } from "react"
import { Scrollbar } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import "swiper/css/bundle"
import Image from "next/image"
import Block from "@/components/wrappers/block"
import { SverigesNatur } from "@/common/logos/SverigesNatur"
import OpenInNew from "@/common/icons/open-in-new"
import { default as ButtonComponent } from "@/common/button/button"
import useBreakpoint from "@/lib/hooks/useBreakpoint"
import Arrow from "@/common/icons/arrow"
import classNames from "classnames"
import styles from "./external-content.module.css"
import { getAnchorAttrsFromLink } from "@/lib/helpers/linkAttrs"

export default function ExternalContent({ block }) {
  const { source, heading, text, blockGap, button, list } = block.attrs
  const { isMounted, breakpoint, isSmallerThan } = useBreakpoint()
  const [contentSwiper, setContentSwiper] = useState(null)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const id = "external-content-" + source
  const [cardWidth, setCardWidth] = useState(340) // 340 | 280

  useEffect(() => {
    const width = isSmallerThan("md", breakpoint) ? 280 : 340

    if (width === cardWidth) {
      return
    }

    setCardWidth(340)

    return () => setCardWidth(320)
  }, [breakpoint, cardWidth, isSmallerThan])

  if (!list || list?.length === 0 || source?.length === 0 || !source) {
    return null
  }

  const settings = {
    onSwiper: (swiper) => {
      setContentSwiper(swiper)
      setIsBeginning(swiper.isBeginning)
      setIsEnd(swiper.isEnd)
    },
    onSlideChange: (swiper) => {
      setIsBeginning(swiper.isBeginning)
      setIsEnd(swiper.isEnd)
    },
    onReachBeginning: () => setIsBeginning(true),
    onReachEnd: () => setIsEnd(true),
    scrollbar: {
      el: ".swiper-scrollbar-" + id,
      draggable: true,
      dragSize: 500,
      snapOnRelease: true
    },
    slidesPerView: "auto",
    spaceBetween: 16,
    breakpoints: {
      768: {
        spaceBetween: 40
      }
    }
  }

  return (
    <Block
      block={block}
      width="bleed"
      gap={blockGap ?? "normal"}
      className={classNames(styles.wrapper, "source-" + source)}
      innerWrapper={true}>
      <div className="content-wrapper">
        <Heading {...{ source, heading }} />
        <Logo {...{ source }} />
        <Text {...{ text }} />
        <Button {...{ source, button, position: "content" }} />
      </div>
      <div className="card-wrapper">
        {isMounted && (
          <Swiper
            {...settings}
            modules={[Scrollbar]}
            id={id}
            className="swiper">
            {list.map((card, index) => {
              return (
                <SwiperSlide
                  className={classNames("slide", {
                    "is-small": cardWidth === 280
                  })}
                  key={id + "-" + index}>
                  <Card {...{ source, card }} />
                </SwiperSlide>
              )
            })}
          </Swiper>
        )}
      </div>
      <div className="navigation">
        <Button {...{ source, button, position: "slider" }} />
        <NavBar {...{ id }} />
        <NavButtons {...{ contentSwiper, isBeginning, isEnd }} />
      </div>
    </Block>
  )
}

const Heading = ({ source, heading }) => {
  if (source === "sveriges-natur") {
    return <span className="tag">{heading}</span>
  }

  return <h2>{heading}</h2>
}

const Logo = ({ source }) => {
  if (source !== "sveriges-natur") {
    return
  }

  return <SverigesNatur className="logo" />
}

const Text = ({ text }) => {
  return (
    <div
      className="wysiwyg-content"
      dangerouslySetInnerHTML={{ __html: text }}
    />
  )
}

const Button = ({ source, button, position }) => {
  const { isMounted, breakpoint, isLargerThan, isSmallerThan } = useBreakpoint()

  if (!isMounted || !button?.link?.url || source === "kretswebbar") {
    return
  }

  // Only render in medium, large
  if (position === "content" && isSmallerThan("md", breakpoint)) {
    return
  }

  // Only render in sm
  if (position === "slider" && isLargerThan("sm", breakpoint)) {
    return
  }

  const linkAttrs = getAnchorAttrsFromLink(button?.link)

  return (
    <div className="button-wrapper">
      <ButtonComponent
        theme={button?.theme ?? "lime"}
        className="cta-button"
        tagName="a"
        href={linkAttrs.href}
        target={button?.link?.target}
        nofollow={button?.link?.nofollow}
        sponsored={button?.link?.sponsored}>
        {button.label}
      </ButtonComponent>
    </div>
  )
}

const Card = ({ source, card }) => {
  const [isLoading, setLoading] = useState(true)

  if (!card?.url || !card?.title) {
    return null
  }

  return (
    <a
      href={card.url}
      target="_blank"
      className={classNames("card", source)}
      rel="noreferrer">
      <figure
        className={classNames({
          "is-loading": isLoading
        })}>
        {card?.image?.url && (
          <Image
            src={card?.image?.url}
            sizes={card?.image?.sizes}
            alt={card?.image?.alt}
            quality={90}
            style={{ objectFit: "cover" }}
            onLoadingComplete={() => setLoading(false)}
            fill={true}
            loading="lazy"
          />
        )}
      </figure>
      <div className="content">
        {card?.tag && <span className="tag">{card.tag}</span>}
        <h3>
          {card.title} <OpenInNew />
        </h3>
        <div className="footer">
          {card?.source && <span className="source">{card?.source}</span>}
          {card?.dateLine && <span className="dateline">{card?.dateLine}</span>}
        </div>
      </div>
    </a>
  )
}

const NavBar = ({ id }) => {
  const { isMounted, breakpoint, isSmallerThan } = useBreakpoint()

  if (!isMounted || isSmallerThan("md", breakpoint)) {
    return null
  }

  return <div className={classNames("nav-bar", "swiper-scrollbar-" + id)} />
}

const NavButtons = ({ contentSwiper, isBeginning, isEnd }) => {
  return (
    <div className="buttons">
      <button
        aria-label="Föregående"
        disabled={isBeginning}
        onClick={() => {
          if (!contentSwiper) return
          contentSwiper.slideTo(contentSwiper.activeIndex - 1)
        }}
        className="prev">
        <Arrow />
      </button>
      <button
        aria-label="Nästa"
        disabled={isEnd}
        onClick={() => {
          if (!contentSwiper) return
          contentSwiper.slideTo(contentSwiper.activeIndex + 1)
        }}
        className="next">
        <Arrow />
      </button>
    </div>
  )
}
